import React from 'react';
import classnames from 'classnames';
import {
  Typography
} from '@one-thd/sui-atomic-components';
import { Image } from './Image';
import { List } from './List';
import { TabContent } from './TabContent';
import { Text } from './Text';
import { Tip } from './Tip';

const spacingScale = {
  0: '0rem',
  1: '0.25rem',
  2: '0.5rem',
  3: '0.75rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  8: '2rem'
};

const buildSectionClassnames = ({
  subIndex,
  flexGroup = false,
  hasBorder = false,
  shiftLeft = false
}) => classnames({
  'sui-mb-4': subIndex === 0,
  'sui-mb-2': subIndex === 1,
  'sui-mb-1': subIndex >= 2,
  'sui-ml-6': shiftLeft,
  'sui-flex sui-justify-center sui-gap-4': flexGroup,
  'sui-border-solid sui-border-1 sui-border-primary sui-rounded-base': hasBorder
});

const buildSectionStyle = ({
  marginOverride = null,
  marginTopOverride = null
}) => ({
  ...(marginOverride !== null && spacingScale[marginOverride]
    ? { marginBottom: spacingScale[marginOverride] }
    : {}),
  ...(marginTopOverride !== null && spacingScale[marginTopOverride]
    ? { marginTop: spacingScale[marginTopOverride] }
    : {})
});

const renderTextSection = ({
  content, hasCustomHeader, sectionClassnames, sectionStyle
}) => {
  const {
    id, string: textString, isTitle, isTiny, boldString, linkString, linkUrl
  } = content;
  if (hasCustomHeader && isTitle) {
    return null;
  }
  return (
    <div key={id} className={sectionClassnames} style={sectionStyle}>
      <Text
        text={textString}
        isTitle={isTitle}
        isTiny={isTiny}
        boldText={boldString}
        linkText={linkString}
        linkUrl={linkUrl}
      />
    </div>
  );
};

const renderListSection = ({ content, sectionClassnames, sectionStyle }) => {
  const { id, items } = content;
  return (
    <div key={id} className={sectionClassnames} style={sectionStyle}>
      <List list={items} />
    </div>
  );
};

const renderImageSection = ({
  content, modifiers, sectionClassnames, sectionStyle
}) => {
  const {
    id, src, alt, size, subHeader, subSubHeader
  } = content;
  const {
    sideBySide, stacked, ranges, centered, separator
  } = modifiers;
  return (
    <div key={id} className={sectionClassnames} style={sectionStyle}>
      <Image
        src={src}
        alt={alt}
        size={size}
        subHeader={subHeader}
        subSubHeader={subSubHeader}
        sideBySide={sideBySide}
        stacked={stacked}
        ranges={ranges}
        centered={centered}
        separator={separator}
      />
    </div>
  );
};

const renderTipSection = ({
  content, sectionClassnames, sectionStyle, subIndex, renderDrawer
}) => {
  const {
    id, string: tipString, strings: tipStrings, sections: tipSections = []
  } = content;
  return (
    <div key={id} className={sectionClassnames} style={sectionStyle}>
      <Tip text={tipString} texts={tipStrings}>
        <>{tipSections.length ? renderDrawer(tipSections, undefined, undefined, subIndex + 1) : null}</>
      </Tip>
    </div>
  );
};

const renderSectionsSection = ({
  content, sectionClassnames, sectionStyle, subIndex, selectedTab, setSelectedTab, renderDrawer
}) => {
  const { id, items } = content;
  return (
    <div key={id} className={sectionClassnames} style={sectionStyle}>
      {items.map((item, index) => (
        <React.Fragment key={item.id || index}>
          {renderDrawer([item], selectedTab, setSelectedTab, subIndex + 1)}
        </React.Fragment>
      ))}
    </div>
  );
};

const renderImageSeparatorSection = ({ content, sectionStyle }) => {
  const { id, items, separator: separatorString } = content;
  const wrapperClassnames = 'sui-flex sui-items-stretch sui-justify-center sui-gap-8 sui-mb-6 sui-max-w-[75%]';
  const imageClassnames = 'sui-flex sui-flex-col sui-gap-4 sui-items-center sui-min-w-24 sui-justify-end';
  const isOrSeparator = separatorString === 'or';
  const separatorClassnames = `sui-m-auto sui-pb-6 ${isOrSeparator ? 'sui-text-3xl' : 'sui-text-6xl'}`;
  return (
    <div key={id} className="sui-flex sui-justify-center" style={sectionStyle}>
      <div className={wrapperClassnames}>
        {items.map((item, index) => {
          const showSeparator = !!separatorString && index < items.length - 1;
          const { image } = item;
          const {
            id: imageId, src, alt, height, width, subHeader
          } = image;

          return (
            <React.Fragment key={imageId}>
              <div className={imageClassnames}>
                <img
                  key={`image - ${src}`}
                  src={src}
                  alt={alt}
                  height={height}
                  width={width}
                />
                {subHeader && (
                  <div key={`${subHeader} - ${src}`} className="sui-text-nowrap">
                    <Typography
                      variant="body-xs"
                      component="span"
                    >
                      {subHeader}
                    </Typography>
                  </div>
                )}
              </div>
              {showSeparator && (
                <div key={`separator - ${src}`} className="sui-flex sui-items-center">
                  <span className={separatorClassnames}>
                    {separatorString === 'plus' ? '+' : 'or'}
                  </span>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

const renderTabsSection = ({
  content, sectionClassnames, sectionStyle, selectedTab, setSelectedTab, renderDrawer
}) => {
  const { id, items } = content;
  return (
    <div key={id} className={sectionClassnames} style={sectionStyle}>
      <TabContent
        id={id}
        items={items}
        buildDrawer={(drawerContent, nestedSubIndex) => (
          renderDrawer(drawerContent, undefined, undefined, nestedSubIndex)
        )}
        selectedTab={selectedTab}
        onTabChange={(_tabId, newValue) => { setSelectedTab(newValue); }}
      />
    </div>
  );
};

const SECTION_RENDERERS = {
  text: renderTextSection,
  list: renderListSection,
  image: renderImageSection,
  tip: renderTipSection,
  sections: renderSectionsSection,
  imageSeparator: renderImageSeparatorSection,
  tabs: renderTabsSection
};

export const buildDrawer = (drawerSections, selectedTab, setSelectedTab, subIndex = 0, hasCustomHeader = false) => {
  return drawerSections.map((section) => {
    if (Array.isArray(section)) {
      return buildDrawer(section, undefined, undefined, subIndex + 1);
    }

    const [[type, content], [, modifiers = {}] = []] = Object.entries(section);
    const {
      flexGroup,
      hasBorder,
      shiftLeft,
      marginOverride,
      marginTopOverride
    } = modifiers;

    const sectionClassnames = buildSectionClassnames({
      subIndex,
      flexGroup,
      hasBorder,
      shiftLeft
    });
    const sectionStyle = buildSectionStyle({
      marginOverride,
      marginTopOverride
    });

    const renderSection = SECTION_RENDERERS[type] || (() => null);
    return renderSection({
      content,
      modifiers,
      sectionClassnames,
      sectionStyle,
      subIndex,
      selectedTab,
      setSelectedTab,
      hasCustomHeader,
      renderDrawer: buildDrawer
    });
  });
};
